import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BlogAPI } from '../../api/api';

export default function BlogPreview() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const data = await BlogAPI.getAll();
        const articlesWithImages = data.slice(0, 3).map((article) => ({
          ...article,
          imagenPortada: article.imagenPortada
            ? `data:image/jpeg;base64,${article.imagenPortada}`
            : null,
        }));
        setArticles(articlesWithImages);
      } catch (error) {
        console.error('Error fetching articles:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, []);

  if (loading) {
    return (
      <section className="section-padding bg-white">
        <div className="container-narrow">
          <div className="text-center mb-16">
            <div className="animate-pulse">
              <div className="h-4 bg-[var(--color-inerva-mint)] rounded w-20 mx-auto mb-4"></div>
              <div className="h-10 bg-[var(--color-inerva-mint)] rounded w-64 mx-auto mb-4"></div>
              <div className="h-5 bg-[var(--color-inerva-mint)] rounded w-96 mx-auto"></div>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[4/3] rounded-xl bg-[var(--color-inerva-mint)] mb-5"></div>
                <div className="h-4 bg-[var(--color-inerva-mint)] rounded w-24 mb-3"></div>
                <div className="h-6 bg-[var(--color-inerva-mint)] rounded w-full mb-2"></div>
                <div className="h-4 bg-[var(--color-inerva-mint)] rounded w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (articles.length === 0) {
    return null;
  }

  return (
    <section id="blog" className="section-padding bg-white">
      <div className="container-narrow">
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-medium tracking-widest text-[var(--color-inerva-forest)] opacity-70 mb-4 uppercase">
            Blog
          </span>
          <h2 className="mb-4">Últimas reflexiones</h2>
          <p className="text-[var(--color-inerva-text-light)] max-w-2xl mx-auto">
            Artículos sobre bienestar emocional, salud mental y herramientas para tu día a día.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.map((article, index) => (
            <Link
              key={article.id}
              to={`/blog/${article.slug}`}
              className="group block"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <article className="h-full">
                <div className="aspect-[4/3] rounded-xl overflow-hidden mb-5 bg-[var(--color-inerva-mint)] relative">
                  {article.imagenPortada ? (
                    <>
                      <img
                        src={article.imagenPortada}
                        alt={article.titulo}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </>
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <svg
                        className="w-16 h-16 text-[var(--color-inerva-forest)] opacity-30"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1.5}
                          d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                        />
                      </svg>
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-3 text-xs text-[var(--color-inerva-text-light)] mb-2">
                  {article.categoria && (
                    <span className="px-2 py-1 bg-[var(--color-inerva-blush)] rounded-full">
                      {article.categoria}
                    </span>
                  )}
                  {article.fechaPublicacion && (
                    <span className="uppercase tracking-wide">
                      {new Date(article.fechaPublicacion).toLocaleDateString('es-ES', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </span>
                  )}
                </div>

                <h3 className="text-lg font-serif mb-2 text-[var(--color-inerva-forest)] group-hover:text-[var(--color-inerva-forest-light)] transition-colors">
                  {article.titulo}
                </h3>

                <p className="text-sm text-[var(--color-inerva-text-light)] line-clamp-2 mb-3">
                  {article.extracto || article.contenido?.substring(0, 150)}
                </p>

                <span className="text-sm font-medium text-[var(--color-inerva-forest)] inline-flex items-center gap-2 group-hover:gap-3 transition-all">
                  Leer más
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </article>
            </Link>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link to="/blog" className="btn-secondary">
            Ver todos los artículos
          </Link>
        </div>
      </div>
    </section>
  );
}
